import type { Maybe } from '@adornis/base/utilTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { type BuchungsheldPlugin } from '@adornis/bh-plugin-system/plugin-types.js';
import { getDefaultPlugin, getPluginForScope, type GetPluginFactory } from '@adornis/bh-plugin-system/plugin.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import type { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import type { ColorType } from '@adornis/chemistry/elements/theming/coloring-system.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import type { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import type { Price } from '@adornis/payments/db/prices.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import type { Observable } from 'rxjs';
import type { BHFilter } from './entities/bh-filter.js';
import type { BHPayment } from './entities/BHPayment.js';
import type { ProductEntryRequirement } from './entities/ProductEntryRequirement.js';
import type { Product } from './entities/products.js';
import type { ProductWaitinglistPriorityRule } from './entities/ProductWaitinglistPriorityRule.js';
import type { UserStammdaten } from './entities/stammdaten.js';
import type { User } from './entities/user.js';
import type { Workshop } from './entities/workshop.js';
import type { ParticipationState } from './enums/enums.js';
import type { BHTranslationDictionary } from './translations/translation.js';
import type { ParticipationDataview } from './use-case-entities/ParticipationsDataview.js';

export type ActionItem = {
  icon: string;
  text: string;
  disabled?: boolean;
  checked?: boolean | 'mixed';
  action: () => Promise<void>;
};

// ***********************************
// ------- plugin types ---------------
// ***********************************

type UIPlugin = BuchungsheldPlugin<
  'general::ui',
  {
    render: (...args: any[]) => Renderable;
  }
>;

export type RegistrationUserDataPlugin<Tstammdaten extends UserStammdaten = UserStammdaten> = BuchungsheldPlugin<
  'registration::user-data',
  {
    extraUserFields?: string[][];
    // top-level keys from extraUserFields that must be filled before continuing the registration
    requiredExtraUserFields?: string[];
    render: (
      stammdatenController: GenericFieldFormController<Tstammdaten>,
      translationController: TranslationController<BHTranslationDictionary>,
      user: User,
      context?: 'registration' | 'profile',
    ) => Renderable;
  }
>;

export type RegistrationStammdatenPlugin = BuchungsheldPlugin<
  'registration::stammdaten',
  {
    disableCustomFormfields: boolean;
    createStammdaten: (options: {
      userID?: Maybe<string>;
      institutionID?: Maybe<string>;
      createInDB?: Maybe<boolean>;
    }) => Promise<UserStammdaten>;
    customValidate?: (stammdaten: any) => string | undefined;
    getFields: (institutionID: string) => string[];
    classType: () => typeof UserStammdaten;
  }
>;

export type RegistrationProductEntryRequirementsPlugin = BuchungsheldPlugin<
  'registration::product-entry-requirements',
  {
    getRequirementKinds: () => (typeof ProductEntryRequirement)[];
  }
>;

export type RegistrationProductWaitinglistPriorityRulesPlugin = BuchungsheldPlugin<
  'registration::product-waitinglist-priority-rules',
  {
    getRuleKinds: () => (typeof ProductWaitinglistPriorityRule)[];
    blockTimeForLowPriorityInDays: number;
  }
>;

export type PaymentPlugin<
  TpaymentMethods extends string = string,
  TpriceGroups extends string = string,
> = BuchungsheldPlugin<
  'payment',
  {
    paymentMethods: TpaymentMethods[];
    getProductPrice: (
      productID: string,
      userID: string,
    ) => Promise<Maybe<NarrowedEntityData<Price, { _id: 1; unitAmount: 1; currency: 1 }>>>;
    createPayment: (participationIDs: string[], paymentMethod: TpaymentMethods) => Promise<string[]>; // returns paymentIDs (sometimes multiple payments have to be created)
    pay: (paymentID: string, paymentMethod: TpaymentMethods) => Promise<void>;
    afterEnrollment: (participationID: string) => Promise<void>; // should only be called for state IN (not when only WAITING; But it should also be called when WAITING->IN)
    cancleEnrollment: (paymentID: string) => Promise<void>;
    sendInvoice: (invoiceID: string) => Promise<boolean>;
    onPaymentUpdate?: (
      institutionID: string,
      paymentOld: EntityData<BHPayment> | undefined,
      paymentNew: EntityData<BHPayment>,
    ) => Promise<void>;
    priceGroups: TpriceGroups[];
    internalPaymentExportConfig?: {
      invoiceReceiverEnum: string;
      costCenterReceiver: string;
      kostenart: string;
    };
  }
>;

export type InvoiceManagementPlugin<Stammdaten extends UserStammdaten = UserStammdaten> = BuchungsheldPlugin<
  'invoice-management',
  {
    render: (stammdaten: Stammdaten, scope: ChemistryLitElement) => Renderable;
  }
>;

export type InvoiceUserInvoiceTablePlugin = BuchungsheldPlugin<
  'invoice-user-invoice-table',
  {
    render: (
      host: ChemistryLitElement,
      translation: TranslationController<BHTranslationDictionary>,
      userID: string,
      institutionID: string,
    ) => Renderable;
  }
>;

export type WorkshopParticipationExportPlugin = BuchungsheldPlugin<
  'workshop-participation-export',
  {
    render: (
      host: ChemistryLitElement,
      translation: TranslationController<BHTranslationDictionary>,
      institutionID: string,
      workshop: NarrowedEntity<Workshop, { _id: 1; name: 1; appointments: { start: 1; end: 1 } }>,
    ) => Renderable;
  }
>;

export type InstitutionStatisticsPlugin = BuchungsheldPlugin<
  'institution-statistics',
  {
    render: (filter: BHFilter) => Renderable;
  }
>;

export type ParticipationOverviewPlugin = BuchungsheldPlugin<
  'participation-overview',
  {
    getDropdownItemActions: (
      host: any,
      targetDataList: ParticipationDataview[],
      paymentActive: boolean,
    ) => ActionItem[];
    getColumnDefinitions: (
      host: any,
      product: Product,
      coloring: ColorType,
      paymentActive: boolean,
      participationState: ParticipationState,
      extraColumns: ColumnDefinition<typeof ParticipationDataview>[],
      operation: (skip: number, limit: number, filter: BHFilter) => (gqlFields) => Observable<ParticipationDataview[]>,
      filter: BHFilter,
      suspensionHandler: (userID: string, institutionID: string, forceSuspendParticipations: string[]) => void,
      deSuspensionHandler: (userID: string, institutionID: string) => void,
    ) => ColumnDefinition<typeof ParticipationDataview>[];
    getCSVFieldsForExport: (stammdatenFields: string[]) => Promise<object[]>;
  }
>;

// ***********************************
// ----------- getter ----------------
// ***********************************

export type BuchungsheldDbPlugin =
  | UIPlugin
  | RegistrationUserDataPlugin
  | RegistrationStammdatenPlugin
  | RegistrationProductEntryRequirementsPlugin
  | RegistrationProductWaitinglistPriorityRulesPlugin
  | PaymentPlugin
  | InvoiceManagementPlugin
  | InvoiceUserInvoiceTablePlugin
  | WorkshopParticipationExportPlugin
  | InstitutionStatisticsPlugin
  | ParticipationOverviewPlugin;

export const getDbPluginForScope = getPluginForScope as GetPluginFactory<BuchungsheldDbPlugin>;
export const getDbDefaultPlugin = getDefaultPlugin as GetPluginFactory<BuchungsheldDbPlugin>;
